import React, { useEffect, useState, useCallback, useRef } from 'react';
import { formatDateLocale } from '../../utils/dateFormat';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePayment } from '../../context/PaymentContext';
import api from '../../utils/api';
import LegalConsentGate from '../../components/LegalConsentGate';
import { PspProviderMark } from '../../components/subscription/PspProviderMark';
import BuyerDetailsDialog from '../../components/BuyerDetailsDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Loader2, CreditCard, CheckCircle, Clock, XCircle,
  AlertTriangle, ShieldCheck, ArrowLeft,
} from 'lucide-react';

// TREATX-CHECKOUT-UX-HARDENING-01 — provider-agnostic checkout UX.
// This component MUST NOT surface any PSP-specific vocabulary
// ("Stripe", "iyzico", etc.). All copy resolves via i18n.

const STATUS_ICONS = {
  created: Clock,
  pending_customer_action: AlertTriangle,
  processing: Clock,
  authorized: ShieldCheck,
  paid: CheckCircle,
  settled: CheckCircle,
  partially_refunded: AlertTriangle,
  refunded: CheckCircle,
  failed: XCircle,
  cancelled: XCircle,
  expired: Clock,
  disputed: AlertTriangle,
};

const STATUS_COLOR = {
  created: 'text-muted-foreground',
  pending_customer_action: 'text-amber-600',
  processing: 'text-blue-600',
  authorized: 'text-blue-600',
  paid: 'text-green-600',
  settled: 'text-green-600',
  partially_refunded: 'text-amber-600',
  refunded: 'text-green-600',
  failed: 'text-red-600',
  cancelled: 'text-muted-foreground',
  expired: 'text-muted-foreground',
  disputed: 'text-red-600',
};

// Provider-agnostic error mapper: converts backend / provider raw
// codes to canonical i18n keys under `checkout.errors.*`. Anything the
// mapper does not recognise falls through to `checkout.errorGeneric`.
function toCanonicalErrorKey(rawErrorLike) {
  const s = (rawErrorLike || '').toString().toLowerCase();
  if (!s) return null;
  if (s.includes('insufficient')) return 'insufficient_funds';
  if (s.includes('declin')) return 'card_declined';
  if (s.includes('expired')) return 'expired_card';
  if (s.includes('invalid') && s.includes('card')) return 'invalid_card';
  if (s.includes('authentication') || s.includes('3ds') || s.includes('3d_secure')) return 'authentication_required';
  if (s.includes('cancel')) return 'cancelled';
  if (s.includes('network') || s.includes('timeout') || s.includes('fetch')) return 'network';
  return 'processing_error';
}

// P0-PAY-STUCK-01 — provider-readiness / routing failures are TERMINAL for
// this booking: retrying cannot succeed until the provider's payment account
// is made settlement-ready. They must close the legal step and show the real
// reason instead of leaving a spinner on screen. Codes come from the existing
// canonical authorities (RoutingErrorCode / PatientChargeBoundaryError /
// entitlement gate) — no new error authority is introduced here.
export const TERMINAL_CHECKOUT_CODES = {
  unsupported_currency: 'currencyNotSettleable',
  unsupported_country: 'providerNotReady',
  unsupported_capability: 'providerNotReady',
  no_eligible_psp: 'providerNotReady',
  no_candidates: 'providerNotReady',
  environment_mismatch: 'providerNotReady',
  production_not_eligible: 'providerNotReady',
  payment_account_missing: 'providerNotReady',
  payment_account_ownership_mismatch: 'providerNotReady',
  payment_account_environment_mismatch: 'providerNotReady',
  account_status_ineligible: 'providerNotReady',
  readiness_not_met: 'providerNotReady',
  override_ineligible: 'providerNotReady',
  routing_context_missing: 'providerNotReady',
  payment_missing_clinic: 'providerNotReady',
  psp_gateway_unavailable: 'providerNotReady',
  psp_routing_error: 'providerNotReady',
  patient_payments_disabled: 'paymentsDisabled',
  // CHECKOUT-COLLECTION-FENCE-01 — the provider already collected this
  // payment; another attempt is refused and retrying cannot help, so the
  // patient is told what is true instead of seeing a generic failure.
  checkout_collection_already_verified: 'collectionAlreadyVerified',
};

// Reads the canonical `code` out of either envelope shape the backend emits:
// `{detail: {code, message}}` (HTTPException) or `{code, message}` (global handler).
export function extractBackendCode(data) {
  if (!data) return null;
  const d = data.detail;
  if (d && typeof d === 'object' && !Array.isArray(d) && d.code) return d.code;
  return data.code || null;
}

// A payment in one of these states describes a COMPLETED collection. It is
// HISTORY: never the target of the next checkout, never the source of the amount
// due, and it must not block starting the next obligation (`canInitiate`
// previously required `!payment`, which dead-ended the journey after a deposit).
const SETTLED_STATUSES = Object.freeze([
  'paid', 'succeeded', 'captured', 'settled', 'refunded', 'partially_refunded',
]);

export default function PaymentPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const orderId = searchParams.get('order_id');
  const quoteId = searchParams.get('quote_id') || searchParams.get('quoteId');
  const paymentId = searchParams.get('payment_id');
  const sessionId = searchParams.get('session_id');
  const cancelled = searchParams.get('cancelled');

  const {
    fetchPayment, fetchOrderSummary, checkPaymentEligibility,
    initiateCheckout, pollCheckoutStatus, setCurrentPayment, resetPaymentContext,
    clearError,
    orderSummary, currentPayment, loading, error,
  } = usePayment();

  const [eligibility, setEligibility] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [localError, setLocalError] = useState(null);
  // An earlier attempt on this collection whose outcome the PSP could not
  // confirm. The server keeps that session and refuses to open a second one, so
  // the patient is offered a status re-check instead of a dead hosted page.
  const [unresolvedAttempt, setUnresolvedAttempt] = useState(null);
  const [recheckBusy, setRecheckBusy] = useState(false);
  const [recheckOutcome, setRecheckOutcome] = useState(null);

  // TREATX-LEGAL-PLATFORM-V1_5 — patient-side legal gate state.
  // `legalStep` becomes true when the user clicks the primary CTA; the
  // legal step then renders and captures the canonical ConsentReceipt[]
  // which is sent as part of the checkout request. NO PSP field is
  // ever placed inside `pendingConsents`.
  const [legalStep, setLegalStep] = useState(false);
  const [pendingConsents, setPendingConsents] = useState(null);

  // Strict duplicate-submission mutex — a ref survives React re-renders
  // and cannot be double-clicked even if setState hasn't committed yet.
  const submitLockRef = useRef(false);
  const pollRef = useRef(false);
  const loadTokenRef = useRef(0);

  // Localised currency formatter — currency is authoritative (from the payment obligation); no EUR default
  const formatCurrency = useCallback((amount, currency = '') => {
    try {
      return new Intl.NumberFormat(i18n.language || 'en', {
        style: 'currency', currency,
      }).format(amount || 0);
    } catch {
      return `${(amount || 0).toFixed(2)} ${currency}`;
    }
  }, [i18n.language]);

  const formatDate = useCallback((dateStr) => {
    if (!dateStr) return '-';
    return formatDateLocale(dateStr);
  }, [i18n.language]);

  const loadData = useCallback(async () => {
    // STALE STATE — a payment/order/quote from a PREVIOUS collection must never
    // decide what this render shows. Clearing first means a settled deposit
    // cannot leak into the instalment that follows it.
    setEligibility(null);
    setLoaded(false);
    setLocalError(null);
    clearError();

    // STALE RESPONSE — these three requests are in flight while the patient can
    // already have moved to another order/payment (a deep link, the next
    // instalment, the return from checkout). Without a sequence token the SLOWER
    // answer for the PREVIOUS context wins and the page shows another
    // collection's amount. Only the newest load may publish its result.
    const token = ++loadTokenRef.current;
    const current = () => token === loadTokenRef.current;

    const fetched = paymentId ? await fetchPayment(paymentId) : null;
    if (!current()) return;
    // RETURN FROM HOSTED CHECKOUT — the PSP sends the patient back with only a
    // payment_id, so the order/quote must be resolved FROM THE PAYMENT before
    // the canonical schedule can be refreshed. Without this the page came back
    // with no eligibility at all and the next collection was unreachable.
    const resolvedOrderId = orderId || fetched?.order_id || null;
    const resolvedQuoteId = quoteId || fetched?.quote_id || null;
    // NAV-LATENCY-01 — the order summary and the eligibility read are
    // INDEPENDENT (neither feeds the other), but they used to run one after
    // the other, so the page waited for the sum of both. They now run
    // together; the sequence token still decides who may publish, so
    // stale-response protection is unchanged.
    const [, elig] = await Promise.all([
      resolvedOrderId ? fetchOrderSummary(resolvedOrderId) : Promise.resolve(null),
      checkPaymentEligibility(resolvedOrderId, resolvedQuoteId),
    ]);
    if (!current()) return;
    setEligibility(elig);
    setLoaded(true);
  }, [paymentId, orderId, quoteId, fetchPayment, fetchOrderSummary,
      checkPaymentEligibility, clearError]);

  useEffect(() => {
    // Any change of payment/order/quote context drops the previous payment
    // object and its errors before the new context is loaded — and invalidates
    // the in-flight answers for it inside the provider, which is where
    // `currentPayment`/`orderSummary` are actually written.
    resetPaymentContext();
    setPollingStatus(null);
    pollRef.current = false;
    loadData();
  }, [loadData, resetPaymentContext]);

  // Return-from-checkout poll — provider-agnostic.
  useEffect(() => {
    if (!sessionId || !paymentId || pollRef.current) return;
    pollRef.current = true;

    const poll = async (attempts = 0) => {
      const maxAttempts = 6;
      const interval = 2000;

      if (attempts >= maxAttempts) {
        setPollingStatus('timeout');
        return;
      }

      setPollingStatus('polling');
      const result = await pollCheckoutStatus(paymentId);

      if (result?.outcome === 'collected' || result?.internal_state === 'paid') {
        setPollingStatus('paid');
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('session_id');
        newParams.delete('cancelled');
        setSearchParams(newParams, { replace: true });
        // Refresh the canonical schedule: this collection is settled, so the
        // backend now exposes the NEXT payable obligation (or a fully-settled
        // contract). Without this the patient had to reload by hand.
        const elig = await checkPaymentEligibility(
          orderId || result?.payment?.order_id || null,
          quoteId || result?.payment?.quote_id || null,
        );
        setEligibility(elig);
        return;
      }
      // A terminal verdict ends the poll. For iyzico that verdict comes from
      // the PSP (`outcome: not_collected`) — the payment's own state is left to
      // the state machine and is NOT forced to `failed` just to end a poll.
      if (result?.outcome === 'not_collected') {
        setPollingStatus('failed');
        return;
      }
      if (result?.internal_state === 'expired' || result?.internal_state === 'failed') {
        setPollingStatus(result.internal_state);
        return;
      }

      setTimeout(() => poll(attempts + 1), interval);
    };

    poll();
  }, [sessionId, paymentId, pollCheckoutStatus, searchParams, setSearchParams,
      checkPaymentEligibility, orderId, quoteId]);

  // TREATX-LEGAL-PLATFORM-V1_5 — actual PSP-neutral checkout dispatcher.
  // Called AFTER the LegalConsentGate has captured a valid consents
  // payload. Passes consents inline to the backend so the pre-action
  // legal gate can persist evidence keyed by the canonical internal
  // `payment_id` (never a Stripe id) BEFORE any PSP session opens.
  const [buyerGate, setBuyerGate] = useState(null);
  // Structured, actionable failure handed to the legal gate (message + the
  // backend's own code + a safe support reference). Never a card detail.
  const [checkoutFailure, setCheckoutFailure] = useState(null);

  // Resolves a failed checkout response/exception into a user-visible reason.
  // Terminal provider-readiness failures also close the legal step so the CTA
  // never stays in the "Submitting…" state with no explanation.
  // LEGAL-GATE-RETRY-01 — the backend's own structured code and a safe support
  // reference are KEPT (and handed to the legal gate) instead of every failure
  // collapsing into `processing_error`.
  const applyCheckoutFailure = useCallback((data, rawFallback) => {
    const code = extractBackendCode(data);
    const detail = (data && typeof data.detail === 'object' ? data.detail : null) || data || {};
    const reference = detail.payment_id || detail.correlation_id || null;
    const terminalKey = code ? TERMINAL_CHECKOUT_CODES[code] : null;
    if (terminalKey) {
      setLegalStep(false);
      setPendingConsents(null);
      setLocalError(t(`checkout.errors.${terminalKey}`));
      setCheckoutFailure({ message: t(`checkout.errors.${terminalKey}`), code, reference });
      return;
    }
    const serverMessage = typeof detail.message === 'string' ? detail.message : null;
    const key = toCanonicalErrorKey(
      rawFallback || data?.error || (typeof data?.message === 'string' ? data.message : null),
    );
    const message = serverMessage
      || (key ? t(`checkout.errors.${key}`) : t('checkout.errorGeneric'));
    setLocalError(message);
    setCheckoutFailure({ message, code: code || key || null, reference });
  }, [t]);

  const proceedWithCheckout = useCallback(async (consents) => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;
    setCheckoutLoading(true);
    setLocalError(null);
    setCheckoutFailure(null);
    try {
      const country = (eligibility?.country || currentPayment?.country || null);

      // PHASE-2 · THE OBLIGATION IS CHOSEN FIRST, THEN THE PAYMENT.
      // This preferred any known paymentId/currentPayment, so after a deposit
      // settled, the paid deposit payment was reused as the checkout target and
      // the patient was sent to pay the WRONG (already-collected) amount. A
      // settled payment is HISTORY: it is never the target of the next
      // checkout. A pending payment is reused only when it is still payable AND
      // bound to the obligation the backend currently says is due — otherwise a
      // fresh payment is created for that obligation.
      const targetObligationId = eligibility?.payment_obligation_id || null;
      // The candidate is only ever a payment the SERVER returned for THIS
      // context (`fetchPayment` / the context's current payment). A payment_id
      // taken from the URL is an unverified claim: it is used to FETCH, never as
      // a checkout target on its own — a copied or stale link would otherwise
      // open a checkout for another collection, or for another patient's
      // payment, on the server's say-so alone.
      const candidate = (paymentId && currentPayment?.payment_id !== paymentId)
        ? null
        : (currentPayment?.payment_id ? currentPayment : null);
      const candidateSettled = !!candidate
        && SETTLED_STATUSES.includes(candidate.status);
      const candidateObligation = candidate?.metadata?.payment_obligation_id
        || candidate?.payment_obligation_id || null;
      // Reuse requires PROVEN agreement with what is currently payable:
      //  · obligation-backed — the candidate must be bound to the obligation the
      //    backend says is due (it used to be reused whenever no obligation was
      //    known, which is exactly the stale-deposit case);
      //  · standalone — a payment with no obligation binding is only reusable
      //    when the backend reports no payable obligation either.
      // Any other pairing is unverifiable, so a fresh payment is created.
      const bindingAgrees = targetObligationId
        ? candidateObligation === targetObligationId
        : !candidateObligation;
      const candidateReusable = !!candidate
        && !candidateSettled
        && ['created', 'pending_customer_action'].includes(candidate.status)
        && bindingAgrees;
      const pid = candidateReusable ? candidate.payment_id : null;

      if (pid) {
        // IYZ-CF-BUYER-01 — some providers require billing details TreatX does
        // not hold. Ask for them BEFORE opening the session; the server is the
        // authority on what is missing and fails closed if they are absent.
        try {
          const req = await api.get(`/payments/${pid}/buyer-identity/requirements`);
          if (req.data && req.data.complete === false) {
            setBuyerGate({ paymentId: pid, missing: req.data.missing_fields || [], consents });
            return;
          }
        } catch (_e) {
          // Requirements are advisory here; the server still fails closed.
        }

        // Path A — existing payment row. Call domain checkout directly
        // with canonical consents payload.
        const res = await api.post(`/payments/${pid}/checkout`, {
          origin_url: window.location.origin,
          return_path: '/#/patient/payment',
          country_context: country,
          consents: consents || [],
        });
        const data = res.data;
        // A prior attempt whose outcome the PSP could not confirm: there is no
        // usable URL and there must be no second charge opportunity, so the
        // patient is shown the status re-check rather than being redirected.
        if (data?.outcome === 'unresolved') {
          setUnresolvedAttempt({ paymentId: data.payment_id || pid, sessionId: data.session_id || null });
          setRecheckOutcome(null);
          setLegalStep(false);
          setPendingConsents(null);
          return;
        }
        if (data?.success && data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        applyCheckoutFailure(data);
        return;
      }

      if (eligibility?.order_id) {
        // Path B — no payment row yet. Backend creates the row + hands
        // off to the same domain checkout (which now runs the gate).
        const res = await api.post('/patient/payment/initiate', {
          order_id: eligibility.order_id,
          quote_id: eligibility.quote_id,
          origin_url: window.location.origin,
          return_path: '/#/patient/payment',
          country_context: country,
          consents: consents || [],
        });
        const data = res.data;
        if (data?.outcome === 'unresolved') {
          setUnresolvedAttempt({ paymentId: data.payment_id || null, sessionId: data.session_id || null });
          setRecheckOutcome(null);
          setLegalStep(false);
          setPendingConsents(null);
          return;
        }
        if (data?.success && data?.checkout_url) {
          window.location.href = data.checkout_url;
          return;
        }
        applyCheckoutFailure(data);
        return;
      }

      setLocalError(t('checkout.errorGeneric'));
    } catch (e) {
      // Global-handler-wrapped 422 → parse both envelope shapes
      const status = e?.response?.status;
      const data = e?.response?.data || {};
      // FIRST PAYMENT — a resumable buyer-profile refusal. The payment row
      // already exists, so the existing dialog collects the missing statutory
      // fields and THIS SAME payment is retried. The consents captured a moment
      // ago are carried through, so the patient never re-agrees.
      const buyerDetail = (typeof data.detail === 'object' ? data.detail : null) || data;
      if (['buyer_profile_incomplete', 'buyer_address_incomplete']
        .includes(String(buyerDetail?.code || ''))) {
        const resumeId = buyerDetail.payment_id || paymentId || currentPayment?.payment_id;
        if (resumeId) {
          setBuyerGate({ paymentId: resumeId, missing: buyerDetail.missing_fields || [], consents });
          return;
        }
      }
      if (status === 422) {
        let inner = typeof data.detail === 'object' ? data.detail : {};
        if (data.code === 'VALIDATION_ERROR' && typeof data.message === 'string') {
          try { inner = JSON.parse(data.message.replace(/'/g, '"')); } catch (_e) { /* keep */ }
        }
        const missing = inner?.missing || [];
        setLocalError(`Legal evidence required.${missing.length ? ` Missing: ${missing.join(', ')}` : ''}`);
        return;
      }
      applyCheckoutFailure(data, e?.message);
    } finally {
      setCheckoutLoading(false);
      submitLockRef.current = false;
    }
  }, [paymentId, currentPayment, eligibility, t, applyCheckoutFailure]);

  // Single CTA handler — reused for both the "already has payment_id"
  // path and the "fresh eligibility → create payment then redirect"
  // path.  Guarded by ref-mutex so rapid multi-clicks / re-renders /
  // touch double-taps cannot fire two backend calls.
  // TREATX-LEGAL-PLATFORM-V1_5: now opens the legal step first; the
  // real backend call happens ONLY after the user checks each required
  // agreement.
  const handlePrimaryCta = useCallback(async () => {
    if (submitLockRef.current) return;
    setLocalError(null);
    setLegalStep(true);
  }, []);

  // Controlled re-check of an unconfirmed attempt. It uses the payment's OWN
  // PSP read (`checkout-status`), which settles and progresses idempotently if
  // the money was in fact collected — no new session, no new charge.
  const recheckUnresolved = useCallback(async () => {
    const pid = unresolvedAttempt?.paymentId;
    if (!pid || recheckBusy) return;
    setRecheckBusy(true);
    setRecheckOutcome(null);
    try {
      const result = await pollCheckoutStatus(pid);
      // The backend's own outcome contract: `collected` | `not_collected` |
      // `unresolved`. A missing answer (network/5xx) is unresolved, never a
      // non-collection.
      const outcome = result?.outcome;
      if (outcome === 'collected' || result?.settled === true) {
        setRecheckOutcome('collected');
        setUnresolvedAttempt(null);
        const elig = await checkPaymentEligibility(
          orderId || result?.payment?.order_id || null,
          quoteId || result?.payment?.quote_id || null,
        );
        setEligibility(elig);
        return;
      }
      if (outcome === 'not_collected') {
        // The PSP's own terminal verdict: nothing was collected, so a fresh
        // attempt is safe and the existing safeguards allow the replacement.
        setRecheckOutcome('not_collected');
        setUnresolvedAttempt(null);
        return;
      }
      setRecheckOutcome(result?.support_required ? 'support_required' : 'still_unresolved');
    } catch (_e) {
      setRecheckOutcome('still_unresolved');
    } finally {
      setRecheckBusy(false);
    }
  }, [unresolvedAttempt, recheckBusy, pollCheckoutStatus, checkPaymentEligibility,
      orderId, quoteId]);

  if (loading && !loaded) {
    return (
      <div
        data-testid="payment-page-loading"
        role="status"
        aria-live="polite"
        aria-busy="true"
        className="flex items-center justify-center py-16 min-h-[50vh]"
      >
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
        <span className="sr-only">{t('common.loading', 'Loading')}</span>
      </div>
    );
  }

  const payment = currentPayment;
  const StatusIcon = payment ? STATUS_ICONS[payment.status] || Clock : Clock;
  const statusColor = payment ? STATUS_COLOR[payment.status] || 'text-muted-foreground' : '';
  const statusLabel = payment ? t(`checkout.statuses.${payment.status}.label`, {
    defaultValue: payment.status,
  }) : '';
  const statusDescription = payment ? t(`checkout.statuses.${payment.status}.description`, {
    defaultValue: '',
  }) : '';

  // A payment that is already SETTLED describes a COMPLETED collection. It must
  // not be treated as the current one: `canInitiate` required `!payment`, so
  // after paying a deposit the paid payment object stayed in context and the
  // patient could never start the next instalment — the journey dead-ended.
  const paymentIsSettled = !!payment && SETTLED_STATUSES.includes(payment.status);
  // POST-PAYMENT-TRUTH-01 — the payment row on screen is a PREVIOUS collection
  // when it is settled and the backend now names a different obligation as
  // payable. The receipt cards label themselves accordingly instead of sitting
  // unlabelled next to the next instalment's pay button.
  const paymentObligationId = payment?.metadata?.payment_obligation_id
    || payment?.payment_obligation_id || null;
  const isPreviousPayment = paymentIsSettled && !!eligibility?.payment_obligation_id
    && paymentObligationId !== eligibility.payment_obligation_id;
  const fullySettled = !!eligibility?.fully_settled;
  const canPay =
    payment && !paymentIsSettled
    && ['created', 'pending_customer_action'].includes(payment.status)
    && eligibility?.payment_enabled;
  // The backend is the authority on whether anything is still owed.
  const canInitiate = loaded && !fullySettled && (!payment || paymentIsSettled)
    && !error && eligibility?.eligible && eligibility?.payment_enabled;
  const showPrimaryCta = canPay || canInitiate;
  // PHASE-2 · The CTA states the amount of the collection that is about to be
  // taken. A settled payment's amount is history and must not be shown as the
  // amount due: a 2,500 deposit followed by a 1,875 instalment advertised 2,500.
  // PHASE-3 · It must also be the amount of the SAME obligation the checkout
  // will target. A pending payment left over from another collection is not the
  // checkout target (`candidateReusable` rejects it), so quoting its amount
  // would advertise a figure the patient is not about to be charged.
  const paymentTargetsPayable = !!payment && (
    eligibility?.payment_obligation_id
      ? (payment.metadata?.payment_obligation_id || payment.payment_obligation_id)
        === eligibility.payment_obligation_id
      : !(payment.metadata?.payment_obligation_id || payment.payment_obligation_id)
  );
  const primaryAmount = (payment && !paymentIsSettled && paymentTargetsPayable)
    ? formatCurrency(payment.amount, payment.currency)
    : formatCurrency(eligibility?.amount, eligibility?.currency);

  return (
    <div
      data-testid="payment-page"
      className="max-w-2xl mx-auto p-4 sm:p-6 space-y-5 sm:space-y-6"
    >
      {/* Skip / back nav — keyboard-first */}
      <div className="flex items-center gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => window.history.back()}
          data-testid="payment-back-btn"
          aria-label={t('checkout.backButton')}
        >
          <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
          {t('checkout.backButton')}
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">{t('checkout.pageTitle')}</h1>
      </div>

      {(error || localError) && (
        <Card className="border-destructive" role="alert" aria-live="assertive">
          <CardContent className="pt-4">
            <p data-testid="payment-page-error" className="text-sm text-destructive">
              {localError || error}
            </p>
          </CardContent>
        </Card>
      )}

      {/* UNCONFIRMED EARLIER ATTEMPT — the provider could not tell us what
          happened to it, so no new payment was opened and the patient is not
          sent to that (expired or unproven) page. The re-check below asks the
          provider again and settles automatically if the money was taken. */}
      {unresolvedAttempt && (
        <Card
          data-testid="checkout-unresolved-notice"
          className="border-amber-300 bg-amber-50"
          role="alert"
          aria-live="polite"
        >
          <CardContent className="pt-5 space-y-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-semibold text-amber-900">
                  {t('checkout.unresolvedAttempt.title')}
                </p>
                <p className="text-sm text-amber-900">
                  {t('checkout.unresolvedAttempt.description')}
                </p>
                {recheckOutcome === 'still_unresolved' && (
                  <p className="text-sm text-amber-900" data-testid="recheck-still-unresolved">
                    {t('checkout.unresolvedAttempt.stillUnresolved')}
                  </p>
                )}
                {recheckOutcome === 'support_required' && (
                  <p className="text-sm text-amber-900" data-testid="recheck-support-required">
                    {t('checkout.unresolvedAttempt.supportRequired')}
                  </p>
                )}
              </div>
            </div>
            <Button
              data-testid="recheck-payment-status-btn"
              variant="outline"
              onClick={recheckUnresolved}
              disabled={recheckBusy}
            >
              {recheckBusy
                ? t('checkout.unresolvedAttempt.checking')
                : t('checkout.unresolvedAttempt.recheckCta')}
            </Button>
          </CardContent>
        </Card>
      )}

      {recheckOutcome === 'collected' && (
        <Card className="border-emerald-300 bg-emerald-50" role="status" aria-live="polite">
          <CardContent className="pt-4">
            <p className="text-sm text-emerald-900" data-testid="recheck-collected">
              {t('checkout.unresolvedAttempt.collected')}
            </p>
          </CardContent>
        </Card>
      )}

      {recheckOutcome === 'not_collected' && (
        <Card className="border-amber-300 bg-amber-50" role="status" aria-live="polite">
          <CardContent className="pt-4">
            <p className="text-sm text-amber-900" data-testid="recheck-not-collected">
              {t('checkout.unresolvedAttempt.notCollected')}
            </p>
          </CardContent>
        </Card>
      )}


      {/* FULLY SETTLED — the backend reports a contract whose every agreed
          collection has been received. Previously this surfaced as the
          fail-closed "no payable obligation exists right now", which made a
          correctly completed plan look broken. */}
      {fullySettled && (
        <Card
          data-testid="payment-fully-settled"
          className="border-emerald-300 bg-emerald-50"
          role="region"
          aria-labelledby="payment-fully-settled-title"
        >
          <CardContent className="pt-5">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" aria-hidden="true" />
              <div className="min-w-0">
                <h2
                  id="payment-fully-settled-title"
                  className="text-base font-semibold text-emerald-900"
                  data-testid="payment-fully-settled-title"
                >
                  {t('checkout.fullySettled.title')}
                </h2>
                <p className="text-sm text-emerald-900/80 mt-1">
                  {t('checkout.fullySettled.description')}
                </p>
                <p className="mt-2 text-sm text-emerald-900" data-testid="payment-fully-settled-total">
                  {t('checkout.fullySettled.paidLabel')}:{' '}
                  <span className="font-mono">
                    {formatCurrency(eligibility?.settled_total, eligibility?.currency)}
                  </span>
                </p>
                {Array.isArray(eligibility?.obligations) && eligibility.obligations.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-emerald-900/80" data-testid="payment-settled-schedule">
                    {eligibility.obligations.map((o) => (
                      <li key={o.sequence} className="flex justify-between gap-3"
                          data-testid={`payment-settled-obligation-${o.sequence}`}>
                        <span>
                          <span className="font-medium">{o.sequence}.</span>{' '}
                          {o.type === 'installment' && o.installment_number
                            ? t('checkout.obligation.installmentOf', {
                              number: o.installment_number, count: o.installment_count,
                            })
                            : t(`checkout.obligation.${o.type}`, { defaultValue: o.type })}
                          {' · '}
                          {t(`checkout.obligationStatus.${o.status}`, { defaultValue: o.status })}
                        </span>
                        <span className="font-mono">{formatCurrency(o.amount, o.currency)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}


      {eligibility?.eligible && (
        <Card
          data-testid="treatment-reserved-hero"
          className="border-emerald-200 bg-emerald-50/60"
          role="region"
          aria-labelledby="treatment-reserved-title"
        >
          <CardContent className="pt-5">
            <div className="flex items-start gap-3">
              <CheckCircle className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex-1">
                <h2
                  id="treatment-reserved-title"
                  className="text-lg font-semibold text-emerald-900"
                  data-testid="treatment-reserved-title"
                >
                  {/* POST-PAYMENT-TRUTH-01 — the heading followed NO obligation:
                      after the deposit settled and instalment 1 became payable,
                      a patient who had just paid EUR 3,000 was still told
                      "deposit required". The title now names the obligation that
                      is actually payable, with its instalment number. */}
                  {t(
                    `checkout.treatmentReservedTitles.${eligibility.obligation_type || 'deposit'}`,
                    {
                      number: eligibility.installment_number,
                      count: eligibility.installment_count,
                      defaultValue: t('checkout.treatmentReserved'),
                    },
                  )}
                </h2>
                <p className="text-sm text-emerald-800 mt-0.5" data-testid="treatment-reserved-subtitle">
                  {/* The copy follows the obligation that is actually payable.
                      It always said "deposit required", so a patient paying the
                      BALANCE or a later instalment was told to arrange a deposit
                      they had already paid. */}
                  {eligibility.payment_enabled
                    ? t(
                      `checkout.treatmentReservedBy.${eligibility.obligation_type || 'deposit'}`,
                      {
                        treatment: eligibility.treatment,
                        defaultValue: t('checkout.treatmentReservedDeposit', {
                          treatment: eligibility.treatment,
                        }),
                      },
                    )
                    : t('checkout.treatmentReservedNoDeposit', { treatment: eligibility.treatment })}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-emerald-900/80">
                  <span data-testid="treatment-reserved-order-id">
                    {t('checkout.orderLabel')} · <span className="font-mono">{(eligibility.order_id || '').slice(0, 8)}</span>
                  </span>
                  <span data-testid="treatment-reserved-quote-id">
                    {t('checkout.offerLabel')} · <span className="font-mono">{(eligibility.quote_id || '').slice(0, 8)}</span>
                  </span>
                  <span data-testid="treatment-reserved-amount" className="font-semibold">
                    {formatCurrency(eligibility.amount, eligibility.currency)}
                  </span>
                </div>
                {/* PHASE 2 / BALANCE — the collection schedule, straight from
                    backend canonical facts. Every row shows its sequence, type,
                    instalment number/count, amount, paid vs outstanding and
                    status; nothing is computed here. */}
                {Array.isArray(eligibility.obligations) && eligibility.obligations.length > 0 && (
                  <ul className="mt-3 space-y-1 text-xs text-emerald-900/80" data-testid="payment-obligation-schedule">
                    {eligibility.obligations.map((o) => (
                      <li
                        key={o.sequence}
                        data-testid={`payment-obligation-${o.sequence}`}
                        className="flex justify-between gap-3"
                      >
                        <span data-testid={`payment-obligation-label-${o.sequence}`}>
                          <span className="font-medium">{o.sequence}.</span>{' '}
                          {o.type === 'installment' && o.installment_number
                            ? t('checkout.obligation.installmentOf', {
                              number: o.installment_number,
                              count: o.installment_count,
                            })
                            : t(`checkout.obligation.${o.type}`, { defaultValue: o.type })}
                          {' · '}
                          <span data-testid={`payment-obligation-status-${o.sequence}`}>
                            {t(`checkout.obligationStatus.${o.status}`, { defaultValue: o.status })}
                          </span>
                          {Number(o.amount_paid) > 0 && Number(o.remaining_amount) > 0 && (
                            <span data-testid={`payment-obligation-partial-${o.sequence}`}>
                              {' · '}
                              {t('checkout.obligation.partial', {
                                paid: formatCurrency(o.amount_paid, o.currency),
                                outstanding: formatCurrency(o.remaining_amount, o.currency),
                              })}
                            </span>
                          )}
                        </span>
                        <span className="font-mono" data-testid={`payment-obligation-amount-${o.sequence}`}>
                          {formatCurrency(o.amount, o.currency)}
                        </span>
                      </li>
                    ))}
                    <li className="flex justify-between gap-3 border-t border-emerald-200 pt-1" data-testid="payment-obligation-total">
                      <span>{t('checkout.obligation.contractTotal')}</span>
                      <span className="font-mono">{formatCurrency(eligibility.contract_total, eligibility.currency)}</span>
                    </li>
                  </ul>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {pollingStatus === 'polling' && (
        <Card
          className="border-blue-200 bg-blue-50/50"
          role="status"
          aria-live="polite"
          data-testid="payment-verifying"
        >
          <CardContent className="pt-4 flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" aria-hidden="true" />
            <div>
              <span className="text-sm text-blue-800 font-medium">{t('checkout.verifying')}</span>
              <p className="text-xs text-blue-800/80 mt-0.5">{t('checkout.verifyingHint')}</p>
            </div>
          </CardContent>
        </Card>
      )}
      {pollingStatus === 'timeout' && (
        <Card className="border-amber-200 bg-amber-50/50" role="alert">
          <CardContent className="pt-4">
            <p className="text-sm text-amber-800">{t('checkout.verificationTimeout')}</p>
          </CardContent>
        </Card>
      )}
      {cancelled === 'true' && payment?.status !== 'paid' && (
        <Card className="border-amber-200 bg-amber-50/50" role="alert">
          <CardContent className="pt-4">
            <p className="text-sm text-amber-800">{t('checkout.cancelled')}</p>
          </CardContent>
        </Card>
      )}

      {payment && (
        <Card data-testid="payment-status-card">
          <CardContent className="pt-6 text-center space-y-3">
            {/* POST-PAYMENT-TRUTH-01 — this card describes the payment ROW, which
                after a settled deposit is the PREVIOUS collection while the CTA
                above pays the next obligation. It used to be unlabelled, so a
                EUR 2,083.75 instalment button sat beside a EUR 3,000 "Deposit"
                receipt with nothing saying they are different payments. */}
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide"
               data-testid="payment-status-scope">
              {isPreviousPayment ? t('checkout.previousPaymentTitle') : t('checkout.thisPaymentTitle')}
            </p>
            <StatusIcon className={`h-12 w-12 mx-auto ${statusColor}`} aria-hidden="true" />
            <div>
              <h2 data-testid="payment-status-label" className={`text-xl font-semibold ${statusColor}`}>
                {statusLabel}
              </h2>
              {statusDescription && (
                <p className="text-sm text-muted-foreground mt-1">{statusDescription}</p>
              )}
            </div>
            <div className="text-3xl font-bold" data-testid="payment-amount">
              {formatCurrency(payment.amount, payment.currency)}
            </div>
            <Badge variant="outline" className="capitalize" data-testid="payment-type-badge">
              {t(`checkout.obligation.${payment.payment_type}`, { defaultValue: payment.payment_type })}
            </Badge>
            {isPreviousPayment && (
              <p className="text-xs text-muted-foreground" data-testid="payment-previous-hint">
                {t('checkout.previousPaymentHint')}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {buyerGate && (
        <BuyerDetailsDialog
          open
          paymentId={buyerGate.paymentId}
          missingFields={buyerGate.missing}
          onCancel={() => setBuyerGate(null)}
          onComplete={() => {
            const consents = buyerGate.consents;
            setBuyerGate(null);
            proceedWithCheckout(consents);
          }}
        />
      )}

      {showPrimaryCta && legalStep && (
        <Card data-testid="payment-legal-card" className="border-blue-200">
          <CardContent className="p-0">
            <LegalConsentGate
              open
              journeyKey="payment_terms_acceptance"
              countryContext={eligibility?.country || currentPayment?.country || null}
              submitting={checkoutLoading}
              failure={checkoutFailure}
              ctaLabel={t('checkout.legalAcceptContinue', 'Accept & Pay Securely')}
              subtitle={t('checkout.legalSubtitle', 'Please review and accept the applicable agreements before payment.')}
              testIdPrefix="patient-payment-legal"
              onCancel={() => { setLegalStep(false); setPendingConsents(null); }}
              onAccept={({ consents, noneRequired }) => {
                setPendingConsents(consents || []);
                if (noneRequired) {
                  proceedWithCheckout([]);
                } else {
                  proceedWithCheckout(consents);
                }
              }}
            />
          </CardContent>
        </Card>
      )}

      {showPrimaryCta && !legalStep && (
        <>
          {/* Why this payment — provider-neutral explanation. Kept minimal
              and aligned with the existing booking/payment behaviour;
              no legal/refund claims are made here. */}
          <Card
            data-testid="payment-why-panel"
            className="border-slate-200"
            role="region"
            aria-labelledby="payment-why-title"
          >
            <CardContent className="pt-4 pb-4">
              <h3
                id="payment-why-title"
                data-testid="payment-why-title"
                className="text-sm font-semibold text-secondary mb-2"
              >
                {t('checkout.why.title')}
              </h3>
              <ul className="space-y-1.5 text-sm text-foreground/80" aria-labelledby="payment-why-title">
                <li className="flex items-start gap-2" data-testid="payment-why-point-1">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
                  <span>{t('checkout.why.point1')}</span>
                </li>
                <li className="flex items-start gap-2" data-testid="payment-why-point-2">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
                  <span>{t('checkout.why.point2')}</span>
                </li>
                <li className="flex items-start gap-2" data-testid="payment-why-point-3">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" aria-hidden="true" />
                  <span>{t('checkout.why.point3')}</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          {/* WHO collects this payment — the provider's OWN official mark,
              shown before the hosted form takes over. It is driven by the
              backend's own provider field, so another PSP's checkout never
              shows the iyzico mark and an unknown provider shows nothing. */}
          <PspProviderMark
            provider={currentPayment?.provider_name || currentPayment?.psp_provider
              || orderSummary?.psp_provider || eligibility?.psp_provider || null}
          />

          {/* Secure payment trust panel — TreatX branding only.  No PSP
              names, no card-scheme logos, no compliance badges. */}
          <Card
            data-testid="payment-trust-panel"
            className="border-emerald-200 bg-emerald-50/40"
            role="region"
            aria-labelledby="payment-trust-title"
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <ShieldCheck
                  className="h-5 w-5 text-emerald-700 flex-shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <div className="flex-1 space-y-1">
                  <h3
                    id="payment-trust-title"
                    data-testid="payment-trust-title"
                    className="text-sm font-semibold text-emerald-900"
                  >
                    {t('checkout.trust.title')}
                  </h3>
                  <p className="text-xs text-emerald-900/80" data-testid="payment-trust-line-1">
                    {t('checkout.trust.line1')}
                  </p>
                  <p className="text-xs text-emerald-900/80" data-testid="payment-trust-line-2">
                    {t('checkout.trust.line2')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="pay-now-card">
          <CardContent className="pt-6">
            <Button
              data-testid={canPay ? 'pay-now-btn' : 'initiate-payment-btn'}
              className="w-full h-12 text-base font-semibold"
              onClick={handlePrimaryCta}
              disabled={checkoutLoading || submitLockRef.current}
              aria-busy={checkoutLoading}
              aria-label={
                checkoutLoading
                  ? t('checkout.processing')
                  : t('checkout.primaryCtaShort', { amount: primaryAmount })
              }
            >
              {checkoutLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" aria-hidden="true" />
                  <span>{t('checkout.processing')}</span>
                </>
              ) : (
                <>
                  <ShieldCheck className="h-5 w-5 mr-2" aria-hidden="true" />
                  <span>{t('checkout.primaryCtaShort', { amount: primaryAmount })}</span>
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground text-center mt-2" data-testid="payment-helper">
              {checkoutLoading ? t('checkout.processingHint') : t('checkout.helper')}
            </p>
          </CardContent>
        </Card>
        </>
      )}

      {payment && (payment.status === 'paid' || payment.status === 'settled' || payment.status === 'created') && (
        <Card data-testid="payment-breakdown-card">
          <CardHeader>
            <CardTitle className="text-base">
              {isPreviousPayment ? t('checkout.previousPaymentTitle') : t('checkout.paymentDetailsTitle')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between" data-testid="payment-breakdown-amount-row">
              <span className="text-muted-foreground">{t('checkout.amount')}</span>
              <span className="font-medium">{formatCurrency(payment.amount, payment.currency)}</span>
            </div>
            <div className="flex justify-between" data-testid="payment-breakdown-type-row">
              <span className="text-muted-foreground">{t('checkout.type')}</span>
              <span className="font-medium">
                {payment.payment_type === 'installment' && payment.metadata?.installment_number
                  ? t('checkout.obligation.installmentOf', {
                    number: payment.metadata.installment_number,
                    count: payment.metadata.installment_count,
                  })
                  : t(`checkout.obligation.${payment.payment_type}`, { defaultValue: payment.payment_type })}
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {orderSummary && (
        <Card data-testid="order-summary-card">
          <CardHeader>
            <CardTitle className="text-base">{t('checkout.orderSummaryTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {/* The totals are shown WITH their currency. A bare "800.01" on a
                euro plan is unreadable, and the backend reports a single
                currency only when every collection shares one. */}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('checkout.totalPaid')}</span>
              <span className="font-medium">
                {formatCurrency(orderSummary.total_paid, orderSummary.currency || '')}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('checkout.totalRefunded')}</span>
              <span>{formatCurrency(orderSummary.total_refunded, orderSummary.currency || '')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('checkout.pendingTransactions')}</span>
              <span>{formatCurrency(orderSummary.total_pending, orderSummary.currency || '')}</span>
            </div>
            {/* POST-PAYMENT-TRUTH-01 — "Pending 0.00" was read as "nothing left
                to pay". The remaining CONTRACT balance and what is payable right
                now are different facts from the amount of an in-flight
                transaction, and all three come from canonical backend figures —
                nothing is computed here. */}
            {Number.isFinite(eligibility?.remaining_total) && (
              <div className="flex justify-between" data-testid="order-summary-remaining-row">
                <span className="text-muted-foreground">{t('checkout.remainingContractBalance')}</span>
                <span className="font-medium">
                  {formatCurrency(eligibility.remaining_total, eligibility.currency || orderSummary.currency || '')}
                </span>
              </div>
            )}
            <div className="flex justify-between" data-testid="order-summary-due-now-row">
              <span className="text-muted-foreground">{t('checkout.dueNowLabel')}</span>
              {/* POST-PAYMENT-TRUTH-02 — five DIFFERENT facts used to collapse
                  into "Nothing payable right now": a failed read, a stated
                  restriction, an amount that is payable, and a genuinely
                  settled plan. A 503 is not a financial statement. */}
              {eligibility === null ? (
                <span className="text-muted-foreground" data-testid="due-now-loading">
                  {t('checkout.sectionLoadingShort', { defaultValue: 'Loading…' })}
                </span>
              ) : eligibility.eligibility_unavailable ? (
                <span className="text-amber-700 text-right" data-testid="due-now-unavailable">
                  {t('checkout.dueNowUnavailable')}
                  <button
                    type="button"
                    onClick={() => loadData()}
                    className="ml-2 underline text-xs"
                    data-testid="due-now-retry"
                  >
                    {t('patient.dashboard.tryAgain')}
                  </button>
                </span>
              ) : eligibility.eligible && Number.isFinite(eligibility.amount) ? (
                <span className="font-medium">
                  {formatCurrency(eligibility.amount, eligibility.currency || '')}
                </span>
              ) : eligibility.code ? (
                <span className="text-amber-700 text-right text-xs max-w-[60%]" data-testid="due-now-restricted">
                  {t(`checkout.ineligible.${eligibility.code}`, { defaultValue: eligibility.reason })}
                </span>
              ) : (
                <span className="font-medium" data-testid="due-now-none">{t('checkout.noneDueNow')}</span>
              )}
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="text-muted-foreground">{t('checkout.status')}</span>
              <Badge variant="outline" className="capitalize" data-testid="order-aggregate-status">
                {t(`checkout.aggregateStatus.${orderSummary.aggregate_status}`,
                  { defaultValue: orderSummary.aggregate_status })}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {payment && payment.events && payment.events.length > 0 && (
        <Card data-testid="payment-timeline-card">
          <CardHeader>
            <CardTitle className="text-base">{t('checkout.timelineTitle')}</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3" aria-label={t('checkout.timelineTitle')}>
              {payment.events.map((event, index) => (
                <li key={event.event_id || index} className="flex items-start gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" aria-hidden="true" />
                  <div className="flex-1">
                    {/* POST-PAYMENT-TRUTH-01 — the timeline printed raw state
                        enums ("pending_customer_action from created") and raw
                        trigger identifiers at patients. The state is now a
                        sentence; the technical identifiers stay on the small
                        diagnostic line. */}
                    <span className="font-medium">
                      {t(`checkout.paymentState.${event.to_status}`, {
                        defaultValue: String(event.to_status || '').replace(/_/g, ' '),
                      })}
                    </span>
                    {event.from_status && (
                      <span className="text-muted-foreground">
                        {' '}
                        {t('checkout.timelineFrom', {
                          status: t(`checkout.paymentState.${event.from_status}`, {
                            defaultValue: String(event.from_status).replace(/_/g, ' '),
                          }),
                        })}
                      </span>
                    )}
                    <div className="text-xs text-muted-foreground">
                      {formatDateLocale(event.occurred_at)} · {event.trigger_source || event.triggered_by}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      )}

      {eligibility && !eligibility.payment_enabled && (
        <Card
          data-testid="payment-preactivation-card"
          className="border-amber-200 bg-amber-50/50"
          role="region"
          aria-labelledby="preactivation-title"
        >
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 text-amber-800">
              <AlertTriangle className="h-4 w-4" aria-hidden="true" />
              <span id="preactivation-title" className="text-sm font-medium">
                {t('checkout.notReadyTitle')}
              </span>
            </div>
            <p className="text-xs text-amber-700 mt-1">
              {t('checkout.notReady')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* The "nothing to pay yet" card must not appear on a plan that is
          FULLY PAID: the patient was shown "You must accept an offer before
          proceeding to payment" directly under "This treatment plan is fully
          paid". A settled plan already states its own outcome above. */}
      {loaded && !payment && !error && !canInitiate && !fullySettled && (
        <Card data-testid="payment-initiate-card">
          <CardContent className="pt-6 text-center space-y-4">
            <CreditCard className="h-12 w-12 mx-auto text-primary" aria-hidden="true" />
            <p data-testid="payment-page-empty" className="text-muted-foreground">
              {eligibility?.eligible
                ? t('checkout.noPaymentRow')
                : ({
                    financial_contract_missing: t('checkout.financialContractMissing'),
                    payment_obligation_unavailable: t('checkout.paymentObligationUnavailable'),
                    payment_obligation_currency_mismatch: t('checkout.paymentAmountUnavailable'),
                    authoritative_currency_missing: t('checkout.authoritativeCurrencyMissing'),
                  }[eligibility?.code]
                  || (eligibility && !eligibility.eligible && eligibility.reason && eligibility.code ? eligibility.reason : null)
                  || t('checkout.acceptOfferFirst'))}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
